import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  schema?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.can-el.com';

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        if (attr === 'name') {
          (el as HTMLMetaElement).name = selector.match(/name="([^"]+)"/)?.[1] || '';
        }
        document.head.appendChild(el);
      }
      (el as HTMLMetaElement).setAttribute('content', value);
    };

    const setMetaByName = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setMetaByProperty = (property: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMetaByName('description', description);
    if (keywords) setMetaByName('keywords', keywords);

    const canonicalHref = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink('canonical', canonicalHref);

    setMetaByProperty('og:title', ogTitle || title);
    setMetaByProperty('og:description', ogDescription || description);
    setMetaByProperty('og:url', ogUrl ? `${SITE_URL}${ogUrl}` : canonicalHref);

    setMetaByName('twitter:title', ogTitle || title);
    setMetaByName('twitter:description', ogDescription || description);

    // Schema.org JSON-LD
    const existingScript = document.querySelector('script[data-seo-schema]');
    if (existingScript) existingScript.remove();

    if (schema) {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-schema', 'true');
      const schemas = Array.isArray(schema) ? schema : [schema];
      script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.querySelector('script[data-seo-schema]');
      if (s) s.remove();
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogUrl, schema]);
}

export { SITE_URL };